/**
 * @see https://advantech-curation.visualstudio.com/Marketplace/_wiki/wikis/Marketplace.wiki/232/Products
 */

// [GET] Popular Keywords

interface IKeyword {
  name: string;
  searchKey: string;
}

// [GET] Resources

interface IResource {
  title: string;
  description: string;
  pageUrl: string;
  imageUrl: string;
  releaseDate: string;
}

interface IProductResources {
  videos: IResource[];
  catalogs: IResource[];
  industryFocuses: IResource[];
  whitePapers: IResource[];
  caseStudies: IResource[];
  news: IResource[];
}

// [GET] Pricing Plans

enum EnumServiceType {
  CLOUD_ENSAAS = 1,
  CLOUD_SELF_MANAGED_SAAS = 2,
  ON_PREMISES_LICENSE = 3,
  SOFTWARE_HARDWARE_BUNDLES = 4,
  CONSULTING_SERVICES = 5,
  LICENSE_UPGRADE = 6,
}

enum EnumBillingCycle {
  ONE_TIME_PURCHASE = 1,
  SUBSCRIPTION_PAYMENT = 2,
  FREE_TRIAL = 3,
  CONTACT_SALES = 4,
}

enum EnumLicenseDeliveryMethod {
  EMAIL_LICENSE = 1,
  ENSAAS_SERVER = 2,
  MICROSOFT_SHAREPOINT = 3,
  SI_CONSOLE_SERVER = 4,
  SINGLE_DEVICE_LICENSE = 5,
  WISE_IOT_PORTAL_LICENSE = 6,
  OEM_LICENSE = 7,
  THIRD_PARTY_SOFTWARE_LICENSE = 8,
  USB_DONGLE_LICENSE = 9,
}

interface IPricingPlan {
  name: string;
  partNumber: string;
  description: string;
  price: number | null;
  currency: string;
  billingCycleId: EnumBillingCycle;
  licenseDeliveryMethodId: EnumLicenseDeliveryMethod;
  isMostPopular: boolean;
  isValidRegion: boolean;
  isShowPrice: boolean;
  isAccountPrice: boolean;
}

interface IPricingServiceType {
  id: EnumServiceType;
  plans: IPricingPlan[];
}

interface IProductPricing {
  serviceType: IPricingServiceType[];
}

// [GET] Product

enum EnumProductMediaType {
  IMAGE = 1,
  BILIBILI = 2,
  POLYV = 3,
  WISTIA = 4,
  YOUTUBE = 5,
  OTHER = 9,
}

interface IProductInfo {
  id: number | null;
  agreementId: number | null;
  banner: IProductInfoBanner;
  announcement: IProductInfoAnnouncement | null;
  overview: IProductInfoOverview;
  capabilities: IProductInfoCapabilities;
  learnMore: IProductInfoLearnMore;
}

interface IProductInfoBanner {
  name: string;
  description: string;
  webBanner: string | null;
  tabletBanner: string | null;
  mobileBanner: string | null;
  isFreeTrial: boolean;
}

interface IProductInfoAnnouncement {
  sectionTitle: string;
  description: string | null;
}

interface IProductInfoOverview {
  sectionTitle: string;
  mediaType: EnumProductMediaType;
  mediaWebThumbnail: string;
  mediaTabletThumbnail: string;
  mediaMobileThumbnail: string;
  videoUrl: string | null;
  features: IProductInfoOverviewFeature[];
}

interface IProductInfoOverviewFeature {
  name: string;
  description: string;
}

interface IProductInfoCapabilities {
  sectionTitle: string;
  items: IProductInfoCapabilityItem[];
}

interface IProductInfoCapabilityItem {
  briefTabName: string;
  name: string;
  description: string;
  webThumbnail: string;
  tabletThumbnail: string;
  mobileThumbnail: string;
}

interface IProductInfoLearnMore {
  sectionTitle: string;
  recommendProducts: IProductInfoLearnMoreItem[];
}

interface IProductInfoLearnMoreItem {
  briefTabName: string;
  name: string;
  description: string;
  webThumbnail: string;
  tabletThumbnail: string;
  mobileThumbnail: string;
  alias: string;
}

// [GET] Pricing Details

enum EnumLanguage {
  EN_US = 'en-US',
  ZH_TW = 'zh-TW',
  JA_JP = 'ja-JP',
  KO_KR = 'ko-KR',
  TH_TH = 'th-TH',
  VI_VN = 'vi-VN',
  ZH_CN = 'zh-CN',
}

interface IPricingInfo {
  datasheetUrl: string | null;
  overview: string;
  additionalInfo: IPricingInfoAdditionalInfo;
}

interface IPricingInfoAdditionalInfo {
  minHardware: IPricingInfoMinHardware;
  protocols: IPricingInfoProtocols;
  availableLanguages: EnumLanguage[];
}

interface IPricingInfoMinHardware {
  operatingSystem: string;
  containerDeployment: string;
  hardware: string;
  displayResolution: string;
  usb: string;
  networkEnvironment: string;
}

interface IPricingInfoProtocols {
  dataAcquisition: string;
  dataForwarding: string;
}

export type {
  IKeyword,
  IPricingInfo,
  IPricingInfoAdditionalInfo,
  IPricingPlan,
  IPricingServiceType,
  IProductInfo,
  IProductInfoAnnouncement,
  IProductInfoBanner,
  IProductInfoCapabilities,
  IProductInfoCapabilityItem,
  IProductInfoLearnMore,
  IProductInfoLearnMoreItem,
  IProductInfoOverview,
  IProductInfoOverviewFeature,
  IProductPricing,
  IProductResources,
  IResource,
};

export {
  EnumBillingCycle,
  EnumLanguage,
  EnumLicenseDeliveryMethod,
  EnumProductMediaType,
  EnumServiceType,
};
